import * as React from 'react';
import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { PhoneNumberMask, SubmitButton } from 'components';
import { useAuthSignupFormManager } from './auth-signup-form.hook';

interface AuthSignupFormProps {
  submitButtonText?: string;
  token?: string;
  email?: string;
  organization_id?: number;
  role?: number;
  next?: (data?: any) => void;
}

export const AuthSignupForm = ({
  submitButtonText = 'Log Me in!',
  token = '',
  email = '',
  organization_id: orgId,
  role,
  next,
}: AuthSignupFormProps) => {
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [{ formManager, loading_submit }] = useAuthSignupFormManager({ token, email, orgId, role, next });

  return (
    <form onSubmit={formManager.handleSubmit}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            label="First name"
            variant="outlined"
            fullWidth
            onChange={formManager.handleChange('first_name')}
            error={!!(formManager.errors.first_name && formManager.touched.first_name)}
            helperText={formManager.touched.first_name ? formManager.errors.first_name : null}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Last name"
            variant="outlined"
            fullWidth
            onChange={formManager.handleChange('last_name')}
            error={!!(formManager.errors.last_name && formManager.touched.last_name)}
            helperText={formManager.touched.last_name ? formManager.errors.last_name : null}
          />
        </Grid>

        {!token && (
          <Grid item xs={12}>
            <TextField
              label="Company Name"
              variant="outlined"
              fullWidth
              onChange={formManager.handleChange('company')}
              error={!!(formManager.errors.company && formManager.touched.company)}
              helperText={formManager.touched.company ? formManager.errors.company : null}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            disabled={!!token}
            value={formManager.values.email}
            onChange={formManager.handleChange('email')}
            error={!!(formManager.errors.email && formManager.touched.email)}
            helperText={formManager.touched.email ? formManager.errors.email : null}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Phone Number"
            placeholder="(000) 000-0000"
            variant="outlined"
            fullWidth
            value={formManager.values.phone}
            name="phone"
            InputProps={{
              inputComponent: PhoneNumberMask as any,
            }}
            onChange={formManager.handleChange('phone')}
            error={!!(formManager.errors.phone && formManager.touched.phone)}
            helperText={formManager.touched.phone ? formManager.errors.phone : null}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl
            variant="outlined"
            fullWidth
            error={!!(formManager.errors.password && formManager.touched.password)}
          >
            <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
            <OutlinedInput
              type={showPassword ? 'text' : 'password'}
              onChange={formManager.handleChange('password')}
              endAdornment={
                <InputAdornment
                  position="end"
                  sx={{
                    marginRight: 0.8,
                  }}
                >
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Password"
            />
            <FormHelperText error>{formManager.touched.password ? formManager.errors.password : null}</FormHelperText>
          </FormControl>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            align: 'center',
            justifyContent: token ? 'flex-start' : 'center',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <SubmitButton
            label={submitButtonText}
            type="submit"
            loading={loading_submit}
            size={token ? 'medium' : 'large'}
            onClick={() => formManager.handleSubmit()}
          />
        </Grid>
      </Grid>
    </form>
  );
};
